import { useEffect, useMemo, useState } from 'react';
import { HexColorInput } from 'react-colorful';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import { Form, Formik } from 'formik';

import { Input, Modal, PaginationArrow } from '@/common';
import { Search } from '@/components/common';
import { Button, ColorPicker, Label, LABEL_TYPE } from '@/components/generic';
import { queryClient } from '@/configuration/react-query';
import { IMAGE, PAGE_SIZE, TEXT_OVERLAY, VIDEO } from '@/constants';
import { VideoThemeClipTypeEnum } from '@/enums/VideoThemeClipEnum';
import { showToast, ToastType } from '@/helpers';
import {
  clipTypesForPayload,
  DEFAULT_IMAGE_CLIP_DURATION,
  getEmptyClipsByType,
  mapApiClipsToComparable,
  mapClipsByTypeToComparable,
  MAX_IMAGE_CLIP_DURATION,
  MIN_IMAGE_CLIP_DURATION
} from '@/helpers/CustomVideoThemeHelper';
import { AssetItem } from '@/pages/Event/Asset/AssetItem';
import { ClipSection } from '@/pages/Event/TributeVideo/CreateVideo/CustomTheme/ClipSection';
import {
  createCustomThemeValidation,
  hasRequiredThemeClips
} from '@/pages/Event/TributeVideo/CreateVideo/CustomTheme/CustomThemeValidation';
import { TextSection } from '@/pages/Event/TributeVideo/CreateVideo/CustomTheme/TextSection';
import { ArrowDownHeavyIcon, ArrowUpHeavyIcon } from '@/resources';
import {
  useCreateCustomVideoThemeMutation,
  useGetCustomVideoThemeById,
  useGetEventAssets,
  useGetStockClips,
  useUpdateCustomVideoThemeMutation
} from '@/services';
import { Asset, HexColor } from '@/types';
import {
  CreateEditCustomThemeProps,
  CustomThemeClipResponse,
  CustomThemeTextResponse,
  CustomVideoThemeCreate,
  CustomVideoThemeEdit,
  CustomVideoThemeTextUpdate,
  EditableThemeText,
  SelectedThemeClip
} from '@/types/CustomVideoThemeTypes';
import { StockClipAsset } from '@/types/StockClipTypes';
import { TestIds } from '@/shared/test-ids';

export const CreateEditCustomTheme = ({
  eventId,
  customThemeId,
  onDone
}: CreateEditCustomThemeProps) => {
  const [t] = useTranslation('common');
  const { ref: introAssetsRef, inView: introAssetsInView } = useInView({
    threshold: 0,
    rootMargin: '200px 0px'
  });
  const [isPickerOpen, setIsPickerOpen] = useState<boolean>(false);
  const [isAssetsModalOpen, setIsAssetsModalOpen] = useState<boolean>(false);
  const [activeClipType, setActiveClipType] = useState<VideoThemeClipTypeEnum>(
    VideoThemeClipTypeEnum.INTRO
  );
  const [clipsByType, setClipsByType] =
    useState<Record<VideoThemeClipTypeEnum, SelectedThemeClip[]>>(getEmptyClipsByType());
  const [editableTexts, setEditableTexts] = useState<EditableThemeText[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
  const [search, setSearch] = useState<string>();
  const [uploadedContent, setUploadedContent] = useState(true);
  const editPage = customThemeId !== undefined;

  const {
    data: eventAssetsData,
    fetchNextPage: fetchNextEventAssets,
    hasNextPage: hasNextEventAssetsPage,
    isFetchingNextPage: isFetchingEventAssetsNextPage
  } = useGetEventAssets(
    eventId ?? '',
    [],
    PAGE_SIZE,
    search,
    [VIDEO, IMAGE, TEXT_OVERLAY],
    undefined,
    true,
    !!eventId && isAssetsModalOpen
  );

  const {
    data: stockClipsData,
    fetchNextPage: stockClipsFetchNextPage,
    isFetchingNextPage: isStockClipsFetchingNextPage,
    hasNextPage: stockClipsHasNextPage
  } = useGetStockClips(
    PAGE_SIZE,
    isAssetsModalOpen && !uploadedContent,
    search,
    undefined,
    false,
    'StockClipName'
  );

  const { data: customThemeData } = useGetCustomVideoThemeById(customThemeId ?? 0);

  const { mutate: createCustomTheme } = useCreateCustomVideoThemeMutation(queryClient, {
    onSuccess: (response) => {
      showToast(ToastType.SUCCESS, t('themeCreated'));
      onDone(response?.data);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  const { mutate: updateCustomTheme } = useUpdateCustomVideoThemeMutation(queryClient, {
    onSuccess: (response) => {
      showToast(ToastType.SUCCESS, t('themeUpdated'));
      onDone(response?.data);
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  const initialValues = useMemo<CustomVideoThemeCreate>(
    () => ({
      name: customThemeData?.data.name ?? '',
      eventId: customThemeData?.data.eventId ?? Number(eventId),
      barsColor: customThemeData?.data.barsColor ? `#${customThemeData.data.barsColor}` : '#ffffff',
      clips: customThemeData?.data.clips ? customThemeData.data.clips : []
    }),
    [customThemeData, eventId]
  );
  const availableEventAssets: Asset[] = uploadedContent
    ? (eventAssetsData?.pages.flatMap((page) => page?.data?.items ?? []) ?? [])
    : (stockClipsData?.pages.flatMap((page) =>
        page?.data?.items?.map((item: { stockClipAsset: StockClipAsset }) => item.stockClipAsset)
      ) ?? []);

  const hasNextAssetsPage = uploadedContent ? hasNextEventAssetsPage : stockClipsHasNextPage;
  const isFetchingAssetsNextPage = uploadedContent
    ? isFetchingEventAssetsNextPage
    : isStockClipsFetchingNextPage;

  const hasRequiredClips = hasRequiredThemeClips(clipsByType);
  const comparableCurrentClips = useMemo(
    () => mapClipsByTypeToComparable(clipsByType),
    [clipsByType]
  );
  const comparableInitialClips = useMemo(
    () =>
      mapApiClipsToComparable((customThemeData?.data?.clips ?? []) as CustomThemeClipResponse[]),
    [customThemeData]
  );
  const hasClipChanges =
    JSON.stringify(comparableCurrentClips) !== JSON.stringify(comparableInitialClips);
  const initialTextTimings = useMemo(
    () =>
      ((customThemeData?.data?.texts ?? []) as CustomThemeTextResponse[])
        .map((text) => ({
          id: text.id,
          startTime: text.startTime,
          endTime: text.endTime
        }))
        .sort((a, b) => a.id - b.id),
    [customThemeData]
  );
  const currentTextTimings = useMemo(
    () =>
      editableTexts
        .map((text) => ({
          id: text.id,
          startTime: text.startTime,
          endTime: text.endTime
        }))
        .sort((a, b) => a.id - b.id),
    [editableTexts]
  );
  const hasTextChanges = JSON.stringify(initialTextTimings) !== JSON.stringify(currentTextTimings);

  useEffect(() => {
    if (!introAssetsInView) {
      return;
    }

    if (uploadedContent && hasNextEventAssetsPage) {
      fetchNextEventAssets();
    } else if (!uploadedContent && stockClipsHasNextPage) {
      stockClipsFetchNextPage();
    }
  }, [
    introAssetsInView,
    uploadedContent,
    hasNextEventAssetsPage,
    stockClipsHasNextPage,
    fetchNextEventAssets,
    stockClipsFetchNextPage
  ]);

  useEffect(() => {
    if (!editPage) {
      setClipsByType(getEmptyClipsByType());
      setEditableTexts([]);
      return;
    }

    const themeClips = (customThemeData?.data?.clips ?? []) as CustomThemeClipResponse[];
    const mappedClips = getEmptyClipsByType();

    themeClips
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .forEach((clip) => {
        const clipType = clip.type as VideoThemeClipTypeEnum;

        if (!mappedClips[clipType]) {
          return;
        }

        mappedClips[clipType].push({
          id: clip.id,
          assetId: clip.assetId,
          assetName: clip.asset?.assetName ?? '',
          assetType: clip.asset?.assetType ?? '',
          duration: clip.duration
        });
      });

    setClipsByType(mappedClips);

    const themeTexts = (customThemeData?.data?.texts ?? []) as CustomThemeTextResponse[];
    setEditableTexts(
      themeTexts
        .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
        .map((text) => ({
          id: text.id,
          value: text.value,
          startTime: text.startTime,
          endTime: text.endTime
        }))
    );
  }, [customThemeData, editPage]);

  const isImageAsset = (assetType: string) =>
    assetType.toLowerCase() === IMAGE.toLowerCase() ||
    assetType.toLowerCase() === TEXT_OVERLAY.toLowerCase();

  const mapAssetToThemeClip = (asset: Asset): SelectedThemeClip => {
    const duration = isImageAsset(asset.assetType)
      ? DEFAULT_IMAGE_CLIP_DURATION
      : (asset.duration ?? 0);

    return {
      id: undefined,
      assetId: asset.id,
      assetName: asset.assetName,
      assetType: asset.assetType,
      duration
    };
  };

  const searchCallback = (query: string) => {
    setSearch(query);
  };

  const handleSubmit = (values: CustomVideoThemeCreate) => {
    if (!hasRequiredThemeClips(clipsByType)) {
      return;
    }

    const cleanBarsColor = values.barsColor.replace(/^#/, '').slice(0, 6);

    if (editPage) {
      const payload: CustomVideoThemeEdit = {
        ...values,
        id: customThemeId,
        barsColor: cleanBarsColor,
        texts: editableTexts.map(
          (text): CustomVideoThemeTextUpdate => ({
            id: text.id,
            startTime: text.startTime,
            endTime: text.endTime
          })
        ),
        clips: clipTypesForPayload.flatMap((clipType) =>
          clipsByType[clipType].map((clip, index) => ({
            id: clip.id,
            type: clipType,
            assetId: clip.assetId,
            order: index + 1,
            duration: clip.duration
          }))
        )
      };
      updateCustomTheme(payload);
    } else {
      const payload: CustomVideoThemeCreate = {
        ...values,
        barsColor: cleanBarsColor,
        clips: clipTypesForPayload.flatMap((clipType) =>
          clipsByType[clipType].map((clip, index) => ({
            type: clipType,
            assetId: clip.assetId,
            order: index + 1,
            duration: clip.duration
          }))
        )
      };
      createCustomTheme(payload);
    }
  };

  const handleRemoveClip = (clipType: VideoThemeClipTypeEnum, assetId: number) => {
    setClipsByType((prev) => ({
      ...prev,
      [clipType]: prev[clipType].filter((clip) => clip.assetId !== assetId)
    }));
  };

  const handleToggleAssetSelection = (asset: Asset) => {
    setSelectedAssets((prevSelected) => {
      const isSelected = prevSelected.some((selectedAsset) => selectedAsset.id === asset.id);

      if (isSelected) {
        return prevSelected.filter((selectedAsset) => selectedAsset.id !== asset.id);
      }

      return [...prevSelected, asset];
    });
  };

  const handleAddSelectedAssets = () => {
    if (!selectedAssets.length) {
      setIsAssetsModalOpen(false);
      return;
    }

    setClipsByType((prev) => {
      const existingAssetIds = new Set(prev[activeClipType].map((clip) => clip.assetId));
      const newClips = selectedAssets
        .filter((asset) => !existingAssetIds.has(asset.id))
        .map(mapAssetToThemeClip);

      return {
        ...prev,
        [activeClipType]: [...prev[activeClipType], ...newClips]
      };
    });

    setSelectedAssets([]);
    setIsAssetsModalOpen(false);
  };

  const handleClipDurationChange = (
    clipType: VideoThemeClipTypeEnum,
    assetId: number,
    value: string
  ) => {
    const parsedValue = Number(value);

    setClipsByType((prev) => ({
      ...prev,
      [clipType]: prev[clipType].map((clip) => {
        if (clip.assetId !== assetId || !isImageAsset(clip.assetType)) {
          return clip;
        }

        return {
          ...clip,
          duration: Number.isNaN(parsedValue)
            ? MIN_IMAGE_CLIP_DURATION
            : Math.min(Math.max(parsedValue, MIN_IMAGE_CLIP_DURATION), MAX_IMAGE_CLIP_DURATION)
        };
      })
    }));
  };

  const openAssetsModalForType = (clipType: VideoThemeClipTypeEnum) => {
    setActiveClipType(clipType);
    setSelectedAssets([]);
    setIsAssetsModalOpen(true);
    setUploadedContent(true);
  };

  const handleTextTimeChange = (textId: number, field: 'startTime' | 'endTime', value: string) => {
    const parsedValue = Number(value);
    const safeValue = Number.isNaN(parsedValue) ? 0 : Math.max(0, parsedValue);

    setEditableTexts((prev) =>
      prev.map((text) => {
        if (text.id !== textId) {
          return text;
        }

        if (field === 'startTime') {
          return {
            ...text,
            startTime: safeValue,
            endTime: Math.max(text.endTime, safeValue)
          };
        }

        return {
          ...text,
          endTime: safeValue < text.startTime ? text.startTime : safeValue
        };
      })
    );
  };

  return (
    <div
      className={`no-scrollbar mt-2 flex h-[50vh] flex-col 2xl:h-[55vh] 3xl:h-[60vh] 4xl:h-[70vh] ${
        isPickerOpen ? 'overflow-visible' : 'overflow-auto'
      }`}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={createCustomThemeValidation}
        enableReinitialize>
        {({ setFieldValue, values, errors, touched, dirty, isValid }) => (
          <Form>
            <Label
              text={editPage ? t('editCustomTheme') : t('createCustomTheme')}
              type={LABEL_TYPE.H2}
              className='mb-4'
            />
            <div className='flex w-full flex-row gap-x-2'>
              <div className='w-1/3 md:w-1/4'>
                <Input
                  id='name'
                  label={t('name')}
                  type={'text'}
                  isRequired
                  errorMessage={touched.name ? errors.name?.toString() : undefined}
                  isInvalid={!!(errors.name && touched.name)}
                />
              </div>
              <div className='w-1/5 md:w-1/6'>
                <Label
                  text={t('barsColor')}
                  className='mb-2.5 !text-black'
                  type={LABEL_TYPE.PARAGRAPH2}
                />
                <div
                  data-testid={TestIds.tributeVideo.customThemeBarsColorTrigger}
                  className='relative flex h-10 cursor-pointer items-center justify-between rounded-xl border border-secondary-blue-gray p-2'
                  onClick={() => setIsPickerOpen(!isPickerOpen)}>
                  <div
                    style={{ backgroundColor: values.barsColor || '#ffffff' }}
                    className={'size-5 rounded-full border-2'}
                  />
                  {isPickerOpen ? (
                    <ArrowUpHeavyIcon className='size-3 stroke-[0.5] text-black' />
                  ) : (
                    <ArrowDownHeavyIcon className='size-3 stroke-[0.5] text-black' />
                  )}
                </div>
                <div className='relative z-50'>
                  {isPickerOpen && (
                    <div className='absolute right-0 top-0 z-[100] mt-2 rounded-md p-2'>
                      <ColorPicker
                        isOpen={isPickerOpen}
                        setIsOpen={setIsPickerOpen}
                        color={(values.barsColor || '#ffffff') as HexColor}
                        onChange={(value) => {
                          setFieldValue('barsColor', value);
                        }}
                        showColorPreview={false}
                      />
                      <HexColorInput
                        color={values.barsColor || '#ffffff'}
                        onChange={(value) => {
                          setFieldValue('barsColor', value);
                        }}
                        data-testid={TestIds.tributeVideo.customThemeBarsColorHexInput}
                        className='absolute -bottom-4 left-0 w-[150px] rounded-md border-2 py-1 text-center outline-none'
                        data-prevent-click-outside={true}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <Label
              text={t('pleaseChooseTheClipsForYourCustomTheme')}
              type={LABEL_TYPE.PARAGRAPH1}
              className='my-2 md:!font-semibold'
            />
            <Label
              text={t('pleaseChooseTheClipsForYourCustomThemeNote')}
              type={LABEL_TYPE.PARAGRAPH3_BOLD}
              className='mb-2 md:!font-semibold'
            />
            <div className='flex w-full flex-col gap-2 lg:flex-row lg:gap-0'>
              <ClipSection
                addButtonText={t('addIntroClips')}
                clips={clipsByType[VideoThemeClipTypeEnum.INTRO]}
                isImageAsset={isImageAsset}
                onDurationChange={(assetId, value) =>
                  handleClipDurationChange(VideoThemeClipTypeEnum.INTRO, assetId, value)
                }
                onRemoveClip={(assetId) => handleRemoveClip(VideoThemeClipTypeEnum.INTRO, assetId)}
                onAddClip={() => openAssetsModalForType(VideoThemeClipTypeEnum.INTRO)}
              />
              <ClipSection
                addButtonText={t('addBackgroundClips')}
                clips={clipsByType[VideoThemeClipTypeEnum.BACKGROUND]}
                isImageAsset={isImageAsset}
                onDurationChange={(assetId, value) =>
                  handleClipDurationChange(VideoThemeClipTypeEnum.BACKGROUND, assetId, value)
                }
                onRemoveClip={(assetId) =>
                  handleRemoveClip(VideoThemeClipTypeEnum.BACKGROUND, assetId)
                }
                onAddClip={() => openAssetsModalForType(VideoThemeClipTypeEnum.BACKGROUND)}
              />
              <ClipSection
                addButtonText={t('addOutroClips')}
                clips={clipsByType[VideoThemeClipTypeEnum.OUTRO]}
                isImageAsset={isImageAsset}
                onDurationChange={(assetId, value) =>
                  handleClipDurationChange(VideoThemeClipTypeEnum.OUTRO, assetId, value)
                }
                onRemoveClip={(assetId) => handleRemoveClip(VideoThemeClipTypeEnum.OUTRO, assetId)}
                onAddClip={() => openAssetsModalForType(VideoThemeClipTypeEnum.OUTRO)}
              />
            </div>
            {editPage && editableTexts.length > 0 && (
              <div className='mt-3 flex w-full flex-col'>
                <Label
                  text={t('themeTextTimings')}
                  type={LABEL_TYPE.PARAGRAPH1}
                  className='mb-3 md:!font-semibold'
                />

                <div className='flex flex-col gap-2 lg:flex-row lg:gap-0'>
                  {editableTexts.map((text) => (
                    <TextSection key={text.id} text={text} onTimeChange={handleTextTimeChange} />
                  ))}
                </div>
              </div>
            )}
            <div className='mt-4 flex w-full flex-row justify-end'>
              <Button
                type='submit'
                disabled={
                  !(
                    isValid &&
                    hasRequiredClips &&
                    values.name.trim() &&
                    (dirty || hasClipChanges || hasTextChanges)
                  )
                }>
                {editPage ? t('editCustomTheme') : t('addCustomTheme')}
              </Button>
            </div>
          </Form>
        )}
      </Formik>

      <Modal
        isOpen={isAssetsModalOpen}
        onClose={() => {
          setIsAssetsModalOpen(false);
          setSelectedAssets([]);
        }}
        modalClasses='w-[900px] border border-secondary-blue-gray'
        height='h-[700px]'
        label='alreadyUsedPhotosAndVideos'
        description={t('pleaseSelectPhotosForYourCustomTheme')}
        hasButton
        buttonText={t('add')}
        buttonClasses='flex justify-end'
        handleSubmit={handleAddSelectedAssets}>
        <div className='flex h-full flex-col overflow-hidden'>
          <div className='mb-2 flex flex-col justify-start gap-x-2'>
            <Search callback={searchCallback} />
            <div className='mt-5 flex w-full flex-row items-center justify-start'>
              <div
                className={`flex w-1/2 cursor-pointer flex-row items-center justify-center border-b-2 pb-2 ${uploadedContent ? 'border-primary-blue font-semibold text-primary-blue' : 'text-secondary-gray'}`}
                onClick={() => setUploadedContent(true)}>
                {t('uploadedContent')}
              </div>
              <div
                className={`flex w-1/2 cursor-pointer flex-row items-center justify-center border-b-2 pb-2 ${uploadedContent ? 'text-secondary-gray' : 'border-primary-blue font-semibold text-primary-blue'}`}
                onClick={() => setUploadedContent(false)}>
                {t('stockClips')}
              </div>
            </div>
          </div>
          <div className='custom-scrollbar flex-1 overflow-y-auto pr-2'>
            <div className='grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4'>
              {availableEventAssets.map((asset: Asset) => (
                <div
                  key={asset.id}
                  className='cursor-pointer'
                  onClick={() => handleToggleAssetSelection(asset)}>
                  <AssetItem
                    asset={asset}
                    key={asset.id}
                    canBeDeleted={false}
                    canBeEdited={false}
                    selectedAsset={undefined}
                    selectedAssets={selectedAssets}
                    multiselect
                    selectedVideoUrl={''}
                    canBeSelected
                  />
                </div>
              ))}
            </div>
            <div ref={introAssetsRef} className='h-1 w-full' />
            {(Boolean(hasNextAssetsPage) || isFetchingAssetsNextPage) && (
              <PaginationArrow
                isFetchingNextPage={isFetchingAssetsNextPage}
                inView={introAssetsInView}
              />
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};
